import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, throwError } from 'rxjs';
import { AuthService } from '../../../app/auth/auth.service';
import { environment } from '../../../environments/environment';
import { CompanyContextService } from '../../company-context.service';
import { AgedDebtOver90DaysDrillDownRow } from '../dashboard/dashboard.service';

export interface AgedDebtOver90DaysResult {
  value: number | null;
  rows: AgedDebtOver90DaysDrillDownRow[];
}

@Injectable({ providedIn: 'root' })
export class AgedDebtOver90DaysService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly apiUrl = environment.agedDebtOver90DaysApiUrl;

  getAgedDebtOver90Days(endDate: string | null): Observable<AgedDebtOver90DaysResult> {
    const compId = this.resolveCompanyId();
    if (compId == null) {
      return throwError(() => new Error('A company must be selected before loading aged debt.'));
    }

    const params = new HttpParams()
      .set('toDate', endDate ?? '')
      .set('asOfDate', endDate ?? '')
      .set('compId', compId)
      .set('reportingCurrencyCode', 'GBP');

    return this.http.get<unknown>(this.apiUrl, { params }).pipe(
      map(response => {
        const sourceRows = this.findMonthlyRows(response);
        const rows = sourceRows
          .map(row => ({
            monthLabel: String(this.read(row, 'MonthLabel', 'monthLabel', 'Month', 'month') ?? ''),
            monthlyRevenue: Number(this.read(row, 'MonthlyRevenue', 'monthlyRevenue') ?? 0),
            cumulativeRevenue: Number(this.read(row, 'CumulativeRevenue', 'cumulativeRevenue') ?? 0),
            outstandingInvoiceCount: Number(this.read(row, 'OutstandingInvoiceCount', 'outstandingInvoiceCount') ?? 0),
            totalOutstandingAtMonthEnd: Number(
              this.read(row, 'TotalOutstandingAtPeriodEnd', 'TotalOutstandingAtMonthEnd', 'totalOutstandingAtMonthEnd') ?? 0
            ),
            agedInvoiceCountOver90Days: Number(
              this.read(row, 'AgedDocumentCountOver90Days', 'AgedInvoiceCountOver90Days', 'agedInvoiceCountOver90Days') ?? 0
            ),
            agedDebtOver90Days: Number(this.read(row, 'AgedDebtOver90Days', 'agedDebtOver90Days') ?? 0),
            agedDebtOver90Percent: this.toNullableNumber(
              this.read(row, 'AgedDebtOver90Percent', 'agedDebtOver90Percent', 'Value', 'value') as number | string | null
            ),
          }))
          .filter(row => row.monthLabel !== '');

        const explicitValue = this.findKpiValue(response);

        return {
          value: explicitValue ?? rows.at(-1)?.agedDebtOver90Percent ?? null,
          rows,
        };
      })
    );
  }

  private findMonthlyRows(value: unknown): Record<string, unknown>[] {
    if (Array.isArray(value)) {
      const rows = value.filter((item): item is Record<string, unknown> =>
        this.isRecord(item) && this.read(item, 'MonthLabel', 'monthLabel', 'Month', 'month') != null
      );
      if (rows.length > 0) return rows;
      for (const item of value) {
        const nested = this.findMonthlyRows(item);
        if (nested.length > 0) return nested;
      }
      return [];
    }

    if (this.isRecord(value)) {
      for (const nestedValue of Object.values(value)) {
        const nested = this.findMonthlyRows(nestedValue);
        if (nested.length > 0) return nested;
      }
    }
    return [];
  }

  private findKpiValue(value: unknown): number | null {
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.findKpiValue(item);
        if (found != null) return found;
      }
      return null;
    }

    if (!this.isRecord(value)) return null;
    const key = String(this.read(value, 'Key', 'key') ?? '').toLowerCase();
    const isMonthlyRow = this.read(value, 'MonthLabel', 'monthLabel', 'Month', 'month') != null;
    const namedValue = isMonthlyRow
      ? null
      : this.read(value, 'AgedDebtOver90Percent', 'agedDebtOver90Percent');
    const kpiValue = key.includes('aged_debt_90') ? this.read(value, 'Value', 'value') : null;
    const directValue = this.toNullableNumber(namedValue as number | string | null);
    if (directValue != null) return directValue;
    const keyedValue = this.toNullableNumber(kpiValue as number | string | null);
    if (keyedValue != null) return keyedValue;

    for (const nestedValue of Object.values(value)) {
      const found = this.findKpiValue(nestedValue);
      if (found != null) return found;
    }
    return null;
  }

  private read(row: Record<string, unknown>, ...names: string[]): unknown {
    const keys = Object.keys(row);
    for (const name of names) {
      const key = keys.find(candidate => candidate.toLowerCase() === name.toLowerCase());
      if (key) return row[key];
    }
    return null;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value == null) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  private resolveCompanyId(): number | null {
    const user = this.auth.session()?.user;
    if (!user) return null;
    return user.isSuperAdmin
      ? this.companyContext.getSelectedCompanyId(user.id) ?? user.compId
      : user.compId;
  }
}
